import React, { useState, useEffect } from "react";
import { Button, MenuItem } from "@material-ui/core";
import classNames from "classnames";
import { size, clone, map, get, last, filter, isEmpty } from "lodash";
import { useForm, Controller } from "react-hook-form";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Grid, Stack, Checkbox, Typography } from "@mui/material";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { TextField } from "./form/TextField";
import { GalleryFormFieldLabel } from "./GalleryFormFieldLabel";
import { GalleryFormSectionHeading } from "./GalleryFormSectionHeading";
import { AddGalleryEventLogoDialog } from "./AddGalleryEventLogoDialog";

const useStyles = makeStyles({
  textField: {
    width: 200,
    color: "black",
    fontWeight: "800",
    fontSize: "12px",
    textAlign: "left",
  },
  eventDropdownTextField: {
    height: 25,
    width: 245,
    padding: "0px 10px",
    background: "#ebebeb",
    "& svg": {
      top: 0,
      right: "-10px",
    },
  },
  smallTextField: {
    right: 25,
    position: "relative",
    margin: "0px 10px 0px 0px",
    background: "#ebebeb",
    height: 23,
    width: 45,
    // textDecoration: "underline",
    //
    "& input": {
      marginTop: 5,
      textAlign: "center",
      textDecoration: "none",
      padding: 0,
    },
  },
});

export const GalleryEventDetailsSection = ({
  control,
  setValue,
  getValues,
  setEventGallery,
  setAlbum,
  field_name_prefix,
  galleryEventOptions,
  albumOptions,
  selectedGallery,
  optionalLowResPriceChecked,
  openAddGalleryEventLogoDialog,
  handleAddGalleryEventLogo,
  handleOptionalLowResPriceChange,
  handleIndexGalleryForFacialRecognition,
  handleCloseCustomGalleryEventLogoDialog,
}) => {
  const classes = useStyles();

  const labelStyling = {
    width: 130,
    fontWeight: 600,
    fontSize: 12,
    color: "#606060",
  };

  const galleryEventDetailRow = {
    //    gap: 5,
    height: 40,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  };

  const galleryEventDetailsRadioButtonStyling = {
    right: 30,
    position: "relative",
  };

  const disabledTextFieldWrapper = {
    width: 190,
    paddingLeft: "10px",
    background: "#ebebeb",
    "& input": {
      top: 2,
      position: "relative",
    },
  };

  const inputStyling = {
    display: "flex",
    padding: "0px 10px",
    background: "#ebebeb",
    width: 253,
    height: 25,
    fontWeight: 400,
    fontSize: 12,
    color: "#606060",
    "& svg.MuiSelect-icon": {
      top: 0,
      left: "105%",
      position: "absolute",
    },
  };

  const eventFieldWrapperStyling = {
    display: "flex",
    alignItems: "center",
  };

  useEffect(() => {
    if (getValues("album") === null && selectedGallery?.album) {
      setValue("album", selectedGallery.album);
    }
  }, [albumOptions]);

  const renderEventField = () => {
    return (
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <GalleryFormFieldLabel label="Event/School" />
          <Typography sx={inputStyling}>
            <Controller
              name="gallery_event"
              control={control}
              render={(fieldProps, onEventValChanged) => (
                <TextField
                  select
                  type="text"
                  disableUnderline
                  variant="standard"
                  fieldProps={fieldProps}
                  className={classes.textField}
                  onEventValChanged={setEventGallery(fieldProps.field.value)}
                >
                  <MenuItem value={null}>
                    <span className={classes.drop}>{"No Event"}</span>
                  </MenuItem>
                  {map(galleryEventOptions, (v, index) => (
                    <MenuItem value={v.value} key={index}>
                      <span className={classes.drop}>{v.label}</span>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderAlbumField = () => {
    const galleryEventRelatedAlbumDropdownOptions = () => {
      const dropdownOptions = filter(
        albumOptions,
        (x) =>
          get(x, ["original_item", "gallery_event"]) ===
          getValues("gallery_event"),
      );
      return dropdownOptions;
    };

    return (
      <Grid item xs={12} my={2}>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <GalleryFormFieldLabel label="Album/Event" />
          <Typography sx={inputStyling}>
            <Controller
              name="album"
              control={control}
              render={(fieldProps, onAlbumChanged) => (
                <TextField
                  select
                  type="text"
                  disableUnderline
                  variant="standard"
                  fieldProps={fieldProps}
                  className={classes.textField}
                  onAlbumChanged={setAlbum(fieldProps.field.value)}
                >
                  <MenuItem value={null}>
                    <span className={classes.drop}>No Album</span>
                  </MenuItem>
                  {map(
                    galleryEventRelatedAlbumDropdownOptions(),
                    (v, index) => (
                      <MenuItem value={v.value} key={index}>
                        <span className={classes.drop}>{v.label}</span>
                      </MenuItem>
                    ),
                  )}
                </TextField>
              )}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderAddGalleryEventLogoField = () => {
    return (
      <Grid item xs={12}>
        <Box my={2} sx={{ display: "flex", gap: "10px" }}>
          <GalleryFormFieldLabel label="Event Logo" />
          <Typography sx={inputStyling}>
            <Checkbox
              size="small"
              checked={getValues("add_gallery_event_logo")}
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<RadioButtonCheckedIcon />}
              onChange={(event) => handleAddGalleryEventLogo(event)}
            />
            <Controller
              name=""
              control={control}
              render={(fieldProps) => (
                <TextField
                  type="text"
                  disableUnderline
                  variant="standard"
                  fieldProps={fieldProps}
                  className={classes.textField}
                  placeholder="Check to Add Logo to Photos"
                />
              )}
            />
          </Typography>
        </Box>
        {openAddGalleryEventLogoDialog && (
          <AddGalleryEventLogoDialog
            setCustomGalleryEventLogo={setValue}
            handleClose={handleCloseCustomGalleryEventLogoDialog}
          />
        )}
      </Grid>
    );
  };

  const renderFacialRecognitionField = () => {
    const isExistingGallery = get(selectedGallery, "id");
    const isAlreadyIndexed = get(selectedGallery, "can_index_for_facial_recognition");
    const isDisabled = isExistingGallery && isAlreadyIndexed;

    return (
      <Grid item xs={12}>
        <Box my={2} sx={{ display: "flex", gap: "10px" }}>
          <GalleryFormFieldLabel label="Facial Recognition" />
          <Typography sx={inputStyling}>
            <Checkbox
              size="small"
              disabled={isDisabled}
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<RadioButtonCheckedIcon />}
              checked={getValues("can_index_for_facial_recognition") || false}
              onChange={(event) =>
                handleIndexGalleryForFacialRecognition(event)
              }
            />
            <Controller
              name="can_index_for_facial_recognition"
              control={control}
              render={(fieldProps) => (
                <TextField
                  type="text"
                  disableUnderline
                  variant="standard"
                  fieldProps={fieldProps}
                  className={classes.textField}
                  value="Check to Activate"
                />
              )}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderCommissionSplitField = () => (
    <Grid item xs={12}>
      <Box my={2} sx={{ display: "flex", gap: "10px" }}>
        <GalleryFormFieldLabel label="Comission Split" />
        <Typography sx={inputStyling}>
          <Box sx={{ width: 30 }}>
            <Controller
              name="gallery_price.event_commission_percentage"
              control={control}
              render={(fieldProps, disabled) => (
                <>
                  <TextField
                    disabled={disabled}
                    type="text"
                    variant="standard"
                    fieldProps={fieldProps}
                    placeholder={"0%"}
                    InputProps={{ disableUnderline: true }}
                  />
                </>
              )}
            />
          </Box>
          <Controller
            name=""
            control={control}
            render={(fieldProps) => (
              <TextField
                disabled
                type="text"
                disableUnderline
                variant="standard"
                fieldProps={fieldProps}
                className={classes.textField}
                placeholder="% Shared with the Event"
              />
            )}
          />
        </Typography>
      </Box>
    </Grid>
    // <Grid my={'5px'} container item xs={12}>
    //   <Grid item xs={5}>
    //     <GalleryFormFieldLabel label='Comission Split'/>
    //   </Grid>
    //   <Grid xs={1}>
    //     <Controller
    //       name='gallery_price.event_commission_percentage'
    //       control={control}
    //       render={(fieldProps, disabled) => (
    //         <>
    //           <TextField
    //             disabled={disabled}
    //             type="text"
    //             variant="standard"
    //             fieldProps={fieldProps}
    //             className={classes.smallTextField}
    //             placeholder={ '0%' }
    //             InputProps={{ disableUnderline: true }}
    //           />
    //         </>
    //       )}
    //   />
    // </Grid>
    // <Grid xs={2}>
    //     <Typography>
    //       <Controller
    //         name=''
    //         control={control}
    //         render={(fieldProps) => (
    //           <TextField
    //             disabled
    //             type='text'
    //             disableUnderline
    //             variant='standard'
    //             fieldProps={ fieldProps }
    //             className={ classes.textField }
    //             placeholder={ '% Shared with the Event' }
    //           />
    //         )}
    //       />
    //     </Typography>
    //   </Grid>
    //   {openAddGalleryEventLogoDialog &&
    //    <AddGalleryEventLogoDialog
    //      setCustomGalleryEventLogo={setValue}
    //      handleClose={handleCloseCustomGalleryEventLogoDialog}
    //    />
    //   }
    // </Grid>
  );

  return (
    <>
      <GalleryFormSectionHeading heading="Event Details" />
      <Box mb={2} />
      {renderEventField()}
      {renderAlbumField()}
      {renderAddGalleryEventLogoField()}
      {renderFacialRecognitionField()}
      {renderCommissionSplitField()}
    </>
  );
};
